import React, { useEffect, useState } from 'react';
import {
  AlertBanner,
  Badge,
  Box,
  Button,
  Icon,
  Tooltip,
  Typography,
} from '@saviynt/design-system';

import { type Rule } from '../../services/types';
import { isEmpty, useInvertedLocalize } from '../../utils';
import { EXTERNAL_RULE_TYPE } from '../../utils/constants';

import TabContent from './TabContent/TabContent';
import invertedMessages from './messages';

import './RuleTabs.css';

const MAX_RULES_ALLOWED = 10;

interface TabsProps {
  rules: Rule[];
  refetchRules: () => void;
  isRulesfetched: boolean;
}

const newRule = {
  ruleKey: -1,
  name: '',
  ruleDescription: "If user's ...",
  type: EXTERNAL_RULE_TYPE,
  status: -1,
  conditions: [
    {
      attributeKey: 0,
      operator: '',
      attribute: '',
      type: 'User',
      name: 'Users',
      values: [],
    },
  ],
} as Rule;

function RuleTabs({ rules, refetchRules, isRulesfetched }: TabsProps) {
  const [updatedRules, setUpdatedRules] = useState(rules);
  const [activeTab, setActiveTab] = useState(rules[0]?.ruleKey);
  const [isNewRuleAdded, setIsNewRuleAdded] = useState(false);
  const [newRuleName, setNewRuleName] = useState<string>();
  const [showRemoveSuccessBanner, setShowRemoveSuccessBanner] = useState(false);
  const [showCriticalBanner, setShowCriticalBanner] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const createdRuleKey = updatedRules.find((rule) => rule.name === newRuleName)
    ?.ruleKey;

  const l = useInvertedLocalize(invertedMessages);

  useEffect(() => {
    setUpdatedRules(rules);
  }, [rules]);

  const handleTabClick = (id: number) => {
    setActiveTab(id);
  };

  const resetActiveTab = () => {
    setActiveTab(rules[0]?.ruleKey);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    index: number
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleTabClick(index);
    }
  };

  const handleNewRuleClick = () => {
    setIsNewRuleAdded(true);
    setUpdatedRules((prevRules) => [...prevRules, newRule]);
  };

  useEffect(() => {
    if (isNewRuleAdded) {
      setActiveTab(-1);
    }
  }, [isNewRuleAdded, updatedRules]);

  useEffect(() => {
    if (isRulesfetched && !isEmpty(newRuleName) && !isEmpty(createdRuleKey)) {
      setActiveTab(createdRuleKey);
      setNewRuleName('');
    }
  }, [isRulesfetched, createdRuleKey, newRuleName]);

  const getMessage = (value: null | number) => {
    let message = '';

    if (value === null) {
      message = l('Users will be classified according to this rule');
    } else if (value >= 0) {
      message = `: ${value} ${l('External Users')}`;
    }

    return message;
  };

  return (
    <Box className='Ext-RuleTabs-wrapper'>
      <AlertBanner
        colorTheme='Success'
        title={l('Rule removed successfully')}
        description=''
        isVisible={showRemoveSuccessBanner}
        onCancel={() => setShowRemoveSuccessBanner(false)}
        isColonVisible={false}
        autoDismissDelay={2000}
        shouldAutoDismiss
        isFloating
        position={{
          type: 'absolute',
          top: '5rem',
          left: '3rem',
        }}
      />
      <AlertBanner
        colorTheme='Critical'
        title={errorMessage}
        description=''
        isVisible={showCriticalBanner}
        onCancel={() => setShowCriticalBanner(false)}
        isColonVisible={false}
        autoDismissDelay={5000}
        shouldAutoDismiss
        isFloating
        position={{
          type: 'absolute',
          top: '5rem',
          left: '3rem',
        }}
      />
      <div className='Ext-RuleTabs'>
        <div className='Ext-RuleTabs-tabs'>
          {updatedRules.map((rule, index) => (
            <div
              key={rule.ruleKey}
              role='button'
              tabIndex={0}
              onKeyDown={(event) => handleKeyDown(event, index)}
              onClick={() => handleTabClick(rule.ruleKey ?? 0)}
              className={`Ext-RuleTabs-tab ${
                activeTab === rule.ruleKey ? 'Ext-RuleTabs-tab--isActive' : ''
              }`}>
              {rule.status === 0 ? (
                <Tooltip
                  trigger={
                    <Badge
                      kind='number'
                      number={index + 1}
                      color='success'
                      size='medium'
                      className='Ext-RuleTabs-tab-badge'
                    />
                  }
                  text={l('Confirmed')}
                  placement='top'
                />
              ) : (
                <Badge
                  kind='number'
                  number={index + 1}
                  badgeStyle='outlined'
                  color='neutral'
                  size='medium'
                  className='Ext-RuleTabs-tab-badge'
                />
              )}
              <div className='Ext-RuleTabs-tab-ruleDetails'>
                <Typography kind='h3' lineLimit={1}>
                  {`${l('Rule')}: ${rule.ruleDescription}`}
                </Typography>
                <Typography
                  kind='body2'
                  className='Ext-RuleTabs-tab-ruleDetails-applies'>
                  {rule.data?.impactedUserCount != null &&
                  rule.data.impactedUserCount >= 0 ? (
                    <>
                      <Typography
                        kind='body2'
                        color='--color-foreground-neutral-subtle'>
                        {`${l('Applies to')} `}
                      </Typography>
                      {getMessage(rule.data.impactedUserCount)}
                    </>
                  ) : (
                    <Typography
                      kind='body2'
                      color='--color-foreground-neutral-subtle'>
                      {getMessage(rule.data?.impactedUserCount ?? null)}
                    </Typography>
                  )}
                </Typography>
              </div>
            </div>
          ))}
          <div className='Ext-RuleTabs-tabs-newRule'>
            {updatedRules.length >= MAX_RULES_ALLOWED && !isNewRuleAdded && (
              <Typography kind='body2' color='--color-foreground-critical-bold'>
                {l(
                  'You have reached the maximum limit of {maxRuleCount} rules.',
                  { maxRuleCount: MAX_RULES_ALLOWED }
                )}
              </Typography>
            )}
            {isNewRuleAdded && (
              <Tooltip
                trigger={
                  <Button
                    type='button'
                    kind='outlined'
                    leftIcon={<Icon kind='Add' />}
                    isDisabled
                    onClick={handleNewRuleClick}>
                    {l('New Rule')}
                  </Button>
                }
                text={l(
                  'A new rule is already in progress. Please complete it before creating another one.'
                )}
                openDelay={0}
              />
            )}
            {updatedRules.length < MAX_RULES_ALLOWED && !isNewRuleAdded && (
              <Button
                type='button'
                kind='outlined'
                leftIcon={<Icon kind='Add' />}
                onClick={handleNewRuleClick}>
                {l('New Rule')}
              </Button>
            )}
          </div>
        </div>

        {updatedRules.length > 0 && (
          <div className='Ext-RuleTabs-content'>
            {updatedRules.map(
              (rule: Rule) =>
                rule.ruleKey === activeTab && (
                  <TabContent
                    rule={rule}
                    isNewRule={rule.ruleKey === -1}
                    refetchRules={refetchRules}
                    resetActiveTab={resetActiveTab}
                    setIsNewRuleAdded={setIsNewRuleAdded}
                    setNewRuleName={setNewRuleName}
                    setShowRemoveSuccessBanner={setShowRemoveSuccessBanner}
                    setErrorMessage={setErrorMessage}
                    setShowCriticalBanner={setShowCriticalBanner}
                  />
                )
            )}
          </div>
        )}
      </div>
    </Box>
  );
}

export default RuleTabs;
